import { loadBaseCSS } from '../shared/button-utils.js';
import { primitiveStyles } from '../shared/primitive-utils.js';

/**
 * Indeterminate activity indicator. Set aria-busy on the region being updated.
 * @element qds-spinner
 * @attr {string} label - Accessible status text. Default Loading.
 * @attr {'sm'|'md'|'lg'} size - Indicator size. Default md.
 * @attr {boolean} decorative - Hide duplicate activity from assistive technology.
 * @csspart indicator - Animated ring; respects prefers-reduced-motion.
 */
export class QdsSpinner extends HTMLElement {
  static get observedAttributes(): string[] { return ['label', 'size', 'decorative']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: inline-flex; vertical-align: middle; }
      [part="indicator"] { display: block; width: 24px; height: 24px; border: 3px solid var(--theme-border-primary, #e4e9f2);
        border-inline-start-color: var(--theme-accent-primary, #445e89); border-radius: 50%; animation: spin .8s linear infinite; }
      :host([size="sm"]) [part="indicator"] { width: 16px; height: 16px; border-width: 2px; }
      :host([size="lg"]) [part="indicator"] { width: 40px; height: 40px; border-width: 4px; }
      .label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
      @keyframes spin { to { transform: rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) { [part="indicator"] { animation: none; } }
      @media (forced-colors: active) { [part="indicator"] { border-color: GrayText; border-inline-start-color: Highlight; } }
    </style><span class="status" role="status"><span part="indicator" aria-hidden="true"></span><span class="label"></span></span>`;
    loadBaseCSS();
  }
  connectedCallback(): void { this._sync(); }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  private _sync(): void {
    const status = this.shadowRoot!.querySelector('.status')!;
    status.setAttribute('aria-hidden', String(this.hasAttribute('decorative')));
    this.shadowRoot!.querySelector('.label')!.textContent = this.getAttribute('label') || 'Loading';
  }
}
if (!customElements.get('qds-spinner')) customElements.define('qds-spinner', QdsSpinner);
